import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import { QRCodeSVG } from "qrcode.react";
import { formatPrice, PAYMENT_LABEL, FULFILLMENT_LABEL } from "@/lib/site";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// 80mm termal fiş — barkod (Code128) + QR. Inline stil (Tailwind'den bağımsız, yazdırma tutarlı).
export default function OrderReceipt({ order, site }) {
  const barRef = useRef(null);
  const code = order?.order_no || order?.id?.slice(0, 10) || "";

  useEffect(() => {
    if (barRef.current && code) {
      try {
        JsBarcode(barRef.current, code, {
          format: "CODE128", width: 1.7, height: 48, displayValue: false, margin: 0,
        });
      } catch { /* ignore */ }
    }
  }, [code]);

  if (!order) return null;
  const isDelivery = (order.fulfillment || "delivery") === "delivery";
  const S = {
    wrap: { width: "80mm", padding: "2mm 3mm", color: "#000", background: "#fff",
      fontFamily: "'Courier New', monospace", fontSize: "12px", lineHeight: 1.35 },
    center: { textAlign: "center" },
    brand: { fontSize: "18px", fontWeight: 700, letterSpacing: "1px" },
    hr: { border: 0, borderTop: "1px dashed #000", margin: "6px 0" },
    row: { display: "flex", justifyContent: "space-between", gap: "6px" },
    label: { color: "#000", fontWeight: 700 },
    big: { fontSize: "15px", fontWeight: 700 },
    small: { fontSize: "10px" },
    th: { display: "flex", justifyContent: "space-between", fontWeight: 700, borderBottom: "1px solid #000", paddingBottom: "2px" },
    item: { padding: "3px 0", borderBottom: "1px dotted #999" },
  };

  return (
    <div className="print-receipt" style={S.wrap}>
      <div style={S.center}>
        <div style={S.brand}>{(site?.brand || "ERTAN KASAP").toUpperCase()}</div>
        <div style={S.small}>Premium Kasap &amp; Market</div>
        {site?.phone_display && <div style={S.small}>{site.phone_display}</div>}
      </div>

      <hr style={S.hr} />
      <div style={{ ...S.row, ...S.big }}>
        <span>SİPARİŞ</span>
        <span>{code}</span>
      </div>
      <div style={S.small}>{fmtDate(order.created_at)}</div>

      <hr style={S.hr} />
      {order.customer_name && (
        <div style={S.row}><span style={S.label}>Müşteri</span><span>{order.customer_name}</span></div>
      )}
      {order.phone && (
        <div style={S.row}><span style={S.label}>Telefon</span><span>{order.phone}</span></div>
      )}
      <div style={S.row}>
        <span style={S.label}>Teslimat</span>
        <span>{FULFILLMENT_LABEL[order.fulfillment] || "Adrese Teslim"}</span>
      </div>
      {isDelivery && order.zone && (
        <div style={S.row}><span style={S.label}>Bölge</span><span>{order.zone}</span></div>
      )}
      {isDelivery && order.address && (
        <div style={{ marginTop: "2px" }}><span style={S.label}>Adres: </span>{order.address}</div>
      )}
      {order.delivery_time && (
        <div style={S.row}><span style={S.label}>Zaman</span><span>{order.delivery_time}</span></div>
      )}
      <div style={{ ...S.row, ...S.big, marginTop: "3px" }}>
        <span>ÖDEME</span>
        <span>{(PAYMENT_LABEL[order.payment] || "Kapıda").toUpperCase()}</span>
      </div>

      <hr style={S.hr} />
      <div style={S.th}>
        <span>Ürün / Miktar</span>
        <span>Tartı/Tutar</span>
      </div>
      {(order.items || []).map((it, i) => (
        <div key={i} style={S.item}>
          <div style={S.row}>
            <span>{it.name}</span>
            <span>______</span>
          </div>
          <div style={{ ...S.row, ...S.small }}>
            <span>{it.quantity}{it.price ? ` · ${formatPrice(it.price)}/${it.unit || "kg"}` : ""}</span>
            <span>{it.line_estimate ? `≈ ${formatPrice(Math.round(it.line_estimate))}` : ""}</span>
          </div>
          {it.note && <div style={S.small}>» {it.note}</div>}
        </div>
      ))}

      {order.total_amount > 0 && (
        <div style={{ marginTop: "6px" }}>
          {order.discount_amount > 0 && (
            <>
              <div style={S.row}><span>Ara toplam</span><span>{formatPrice(Math.round(order.subtotal || 0))}</span></div>
              <div style={S.row}>
                <span>İndirim (%{order.discount_percent})</span>
                <span>-{formatPrice(Math.round(order.discount_amount))}</span>
              </div>
            </>
          )}
          <div style={{ ...S.row, ...S.big, marginTop: "3px" }}>
            <span>TAHMİNİ TOPLAM</span>
            <span>{formatPrice(Math.round(order.total_amount))}</span>
          </div>
        </div>
      )}
      <div style={S.small}>* Tutar kesimde tartılarak kesinleşir.</div>

      {order.order_note && (
        <>
          <hr style={S.hr} />
          <div><span style={S.label}>Not: </span>{order.order_note}</div>
        </>
      )}

      <hr style={S.hr} />
      <div style={S.center}>
        <svg ref={barRef} style={{ maxWidth: "100%" }} />
        <div style={{ ...S.small, marginTop: "2px", letterSpacing: "1px" }}>{code}</div>
        <div style={{ marginTop: "6px", display: "flex", justifyContent: "center" }}>
          <QRCodeSVG value={code} size={72} />
        </div>
        <div style={{ ...S.small, marginTop: "6px" }}>Teşekkür ederiz · Afiyet olsun</div>
      </div>
    </div>
  );
}
